/* PARACELART — initials-only personalization (5 Oct 2026)
   For vow books and other designs where only the couple's initials (and the wedding date) change.
   snippets/pa-initials-fields.liquid renders one box per letter of the design's pattern
   (custom.initials_style, e.g. "A ♥ J") and a date picker that is stitched in the design's style
   (custom.date_style, e.g. "Oct 26, 2024"). This script keeps each box to one capital letter, moves on
   to the next box, fills the hidden properties[Initials] / properties[Wedding date] fields and keeps the
   "We'll stitch exactly" box in step with the choices. No dependencies. */
(() => {
  'use strict';
  const ready = new WeakSet();
  const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const NOT_LETTER = /[^\p{L}]/gu;
  const ROMAN = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];

  function roman(n) {
    let out = '';
    for (const [value, symbol] of ROMAN) while (n >= value) { out += symbol; n -= value; }
    return out;
  }
  const pad = n => String(n).padStart(2, '0');
  // keys are the choices of the custom.date_style metafield
  const DATE_STYLES = {
    'Oct 26, 2024': (y, m, d) => `${SHORT[m - 1]} ${d}, ${y}`,
    'October 26, 2024': (y, m, d) => `${LONG[m - 1]} ${d}, ${y}`,
    'OCTOBER 26, 2024': (y, m, d) => `${LONG[m - 1].toUpperCase()} ${d}, ${y}`,
    '10 · 26 · 2024': (y, m, d) => `${pad(m)} · ${pad(d)} · ${y}`,
    '10.26.2024': (y, m, d) => `${pad(m)}.${pad(d)}.${y}`,
    'X.XXVI.MMXXIV': (y, m, d) => `${roman(m)}.${roman(d)}.${roman(y)}`
  };
  function formatDate(iso, style) {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!parts) return '';
    const format = DATE_STYLES[style] || DATE_STYLES['Oct 26, 2024'];
    return format(Number(parts[1]), Number(parts[2]), Number(parts[3]));
  }
  // a capital letter in the pattern is a slot for one of the shopper's initials (same test as the snippet)
  const isSlot = ch => ch.toUpperCase() === ch && ch.toLowerCase() !== ch;
  const withWord = (name, word) => (!name ? '' : name.toLowerCase().includes(word) ? name : `${name} ${word}`);

  function initForm(form) {
    if (ready.has(form)) return;
    const fieldset = form.querySelector('[data-initials]');
    const threadStep = form.querySelector('.initials-thread');
    if (!fieldset && !threadStep) return;
    ready.add(form);
    const template = fieldset ? fieldset.dataset.template || '' : '';
    const dateStyle = fieldset ? fieldset.dataset.dateStyle || '' : '';
    const boxes = [...form.querySelectorAll('[data-initial-box]')];
    const initialsValue = form.querySelector('[data-initials-value]');
    const date = form.querySelector('[data-wedding-date]');
    const dateValue = form.querySelector('[data-wedding-date-value]');
    const dateIso = form.querySelector('[data-wedding-date-iso]');
    const line = form.querySelector('[data-initials-line]');
    const dateLine = form.querySelector('[data-initials-date]');
    const details = form.querySelector('[data-initials-details]');
    const linenDot = form.querySelector('[data-initials-linen-dot]');
    const threadDot = form.querySelector('[data-initials-thread-dot]');
    const variantSelect = form.querySelector('[data-variant-select]');

    // the design's pattern with each sample letter replaced, in order, by the shopper's letters
    function fill(keepSamples) {
      let i = 0;
      return [...template].map(ch => {
        if (!isSlot(ch)) return ch;
        const typed = boxes[i] ? boxes[i].value : '';
        i += 1;
        return typed || (keepSamples ? ch : '');
      }).join('');
    }

    function update() {
      const anyTyped = boxes.some(box => box.value);
      const allTyped = boxes.length > 0 && boxes.every(box => box.value);
      if (initialsValue) initialsValue.value = allTyped ? fill(false) : '';
      if (line) {
        line.textContent = fill(true);
        line.classList.toggle('is-empty', !anyTyped);
      }
      const iso = date ? date.value : '';
      const shown = formatDate(iso, dateStyle);
      if (dateValue) dateValue.value = shown;
      if (dateIso) dateIso.value = shown ? iso : '';
      if (dateLine) {
        dateLine.textContent = shown || dateStyle;
        dateLine.classList.toggle('is-empty', !shown);
      }
      const linen = form.querySelector('[name="properties[Linen color]"]:checked');
      const thread = form.querySelector('[name="properties[Thread color]"]:checked');
      const pictured = !thread || !thread.dataset.hex;
      if (linenDot) linenDot.style.background = linen ? linen.dataset.hex : '';
      if (threadDot) {
        threadDot.style.background = pictured ? '' : thread.dataset.hex;
        threadDot.classList.toggle('is-pictured', pictured);
      }
      if (details) {
        const current = variantSelect && variantSelect.selectedOptions[0];
        const variantTitle = current && current.dataset.title !== 'Default Title' ? current.dataset.title : '';
        const threadText = pictured ? 'thread as pictured' : withWord(thread.value, 'thread');
        const parts = [variantTitle, withWord(linen ? linen.value : '', 'linen'), threadText].filter(Boolean);
        details.textContent = `${parts.join(' · ')} — embroidered to order`;
      }
    }

    boxes.forEach((box, i) => {
      box.addEventListener('focus', () => { try { box.select(); } catch (e) { /* not selectable */ } });
      box.addEventListener('input', event => {
        const letters = [...box.value.replace(NOT_LETTER, '').toUpperCase()];
        if (letters.length > 1 && event.inputType === 'insertFromPaste') {
          // a pasted "AJ" fills this box and the ones after it
          letters.forEach((letter, j) => { if (boxes[i + j]) boxes[i + j].value = letter; });
          const next = boxes[Math.min(i + letters.length, boxes.length - 1)];
          if (next) next.focus();
        } else {
          box.value = letters.length ? letters[letters.length - 1] : '';
          if (box.value && boxes[i + 1]) boxes[i + 1].focus();
        }
        box.setCustomValidity('');
        update();
      });
      box.addEventListener('keydown', event => {
        if (event.key === 'Backspace' && !box.value && boxes[i - 1]) {
          event.preventDefault();
          boxes[i - 1].value = '';
          boxes[i - 1].focus();
          update();
        } else if (event.key === 'ArrowLeft' && boxes[i - 1]) {
          event.preventDefault();
          boxes[i - 1].focus();
        } else if (event.key === 'ArrowRight' && boxes[i + 1]) {
          event.preventDefault();
          boxes[i + 1].focus();
        }
      });
      box.addEventListener('invalid', () => { box.setCustomValidity(box.value ? '' : 'Please add one letter here.'); });
    });
    if (date) {
      date.addEventListener('invalid', () => {
        date.setCustomValidity(date.validity.valueMissing ? 'Please choose your wedding date.' : '');
      });
      date.addEventListener('input', () => date.setCustomValidity(''));
    }
    form.addEventListener('input', update);
    form.addEventListener('change', update);
    form.addEventListener('submit', update, true);
    update();
  }

  function init(root) { (root || document).querySelectorAll('.personalize-form').forEach(initForm); }
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', () => document.querySelectorAll('.personalize-form').forEach(form => form.dispatchEvent(new Event('change'))));
  init();
})();
